import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId || !token) return;
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.status === 'paid') {
          await refreshUser();
          setStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus('timeout');
      } catch {
        setStatus('error');
      }
    };
    poll();
    // refreshUser intentionally excluded — only re-poll when sessionId/token changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'checking' && (
          <>
            <Loader2 className="h-12 w-12 text-[#0F392B] animate-spin mx-auto" />
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Processing payment...</h1>
            <p className="text-sm text-muted-foreground">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" strokeWidth={1.5} />
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Payment Successful!</h1>
            <p className="text-sm text-muted-foreground">Your account has been upgraded. Welcome to the full Ivy League Consulting experience.</p>
            <Link to="/dashboard">
              <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="go-to-dashboard-btn">Go to Dashboard</Button>
            </Link>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="h-16 w-16 text-red-400 mx-auto" strokeWidth={1.5} />
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Something went wrong</h1>
            <p className="text-sm text-muted-foreground">We couldn't confirm your payment. Please contact support if you were charged.</p>
            <Link to="/pricing">
              <Button variant="outline" data-testid="back-to-pricing-btn">Back to Pricing</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
